/**
 * API client for TEK17 Management API
 *
 * Thin aggregator that composes domain-specific API modules into a single client.
 * All backend calls go through this module via `apiClient`.
 */
import axios from 'axios';
import {createDocsApi} from './docs';
import {createPendingDocsApi} from './pending-docs';
import {createParametersApi} from './parameters';
import {createLocationApi} from './location';
import {createFeedbackApi} from './feedback';
import {createUdeApi} from './ude';
import {createCompaniesApi} from './companies';
import {createLlmApi} from './llm';
import {installCompanyInterceptor} from '../auth/company';
import {installAuthInterceptor} from '../auth/session';

// Re-export all types for backwards compatibility
export type {
    ProjectContext,
    ScopeLevel,
    ApplicableDocument,
    AddressSearchResult,
    Plan,
    Case,
    PropertyContext,
    PlanMetadata,
    Parameter,
    SynthesisResult,
    ParameterResolution,
} from './types';

// API calls use relative URLs — nginx (production) or Vite proxy (dev) forwards to backend
const client = axios.create({
    headers: {
        'Content-Type': 'application/json',
    },
});

// One refresh on an expired token, then the sign-in page (SB-23). Every domain module shares
// this instance, so this one line covers all of them.
installAuthInterceptor(client);
// UDE's company 409s reach every page with a sentence as `detail` (SB-321).
installCompanyInterceptor(client);

// UDE namespace kept separate from the questions-backend methods so a
// `ude.foo()` call site can't accidentally hit the questions API.
export const ude = createUdeApi(client);

export const apiClient = {
    ...createDocsApi(client),
    ...createPendingDocsApi(client),
    ...createParametersApi(client),
    ...createLocationApi(client),
    ...createFeedbackApi(client),
    ...createCompaniesApi(client),
    ...createLlmApi(client),
    ude,
};

export default apiClient;
